/**
 * useIPrescribe — shared hook for the iPrescribe gate + open flow.
 *
 * Extracted from legacy App.js (`canAccessIPrescribe`, `getIPrescribeUrl`,
 * `openIPrescribe`) so V2 surfaces can consume the SAME contract
 * instead of duplicating the gate logic.  This is Phase 3 of the
 * V2 Meds & Vitals migration — Phase 1 (mount native tab) and
 * Phase 2 (extract Med Referral Dialog) shipped separately.
 *
 * Contract
 * ────────
 *   const { canAccess, open, url } = useIPrescribe();
 *
 *   canAccess()  → boolean.  True iff the current user has
 *                 prescribing authority (is_prescriber,
 *                 is_medication_manager, or super admin) AND the
 *                 branding switch `iprescribe_enabled` is on.
 *   open()       → no-op if !canAccess(); otherwise opens the
 *                 configured iPrescribe URL in a new tab.
 *   url          → the resolved iPrescribe URL (used only for
 *                 cases that need to render a static link).
 *
 * Why a hook (not three free functions)
 * ─────────────────────────────────────
 *   Consumers always need the latest user + branding settings,
 *   which are React-context-bound.  A hook gives us auto-rerender
 *   when either changes (e.g. when an admin toggles iPrescribe on
 *   in Branding settings, every consumer's `canAccess()` flips
 *   immediately).  Free functions taking `user` + `brandingSettings`
 *   would force every caller to thread both args through, which is
 *   exactly the smell Phase 3 is meant to eliminate.
 */
import { useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useStaticData } from '../context/StaticDataContext';
import { isSuperAdmin } from '../utils/helpers';

const DEFAULT_IPRESCRIBE_URL = 'https://account.iprescribe.com/';

export function useIPrescribe() {
  const { user } = useAuth();
  const { brandingSettings } = useStaticData();

  const url = useMemo(
    () => brandingSettings?.iprescribe_url || DEFAULT_IPRESCRIBE_URL,
    [brandingSettings?.iprescribe_url],
  );

  const canAccess = useCallback(() => {
    if (!user) return false;
    if (!brandingSettings?.iprescribe_enabled) return false;
    return Boolean(
      user.is_prescriber
        || user.is_medication_manager
        || isSuperAdmin(user),
    );
  }, [user, brandingSettings?.iprescribe_enabled]);

  const open = useCallback(() => {
    if (!canAccess()) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [canAccess, url]);

  return { canAccess, open, url };
}

export default useIPrescribe;
